import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Users, Clock, MessageCircle, Shield, ArrowRight, X, Sparkles, Send } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';

const API = process.env.REACT_APP_BACKEND_URL;

function CurrencyToggle({ currency, setCurrency }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid="club-currency-toggle">
      <span className="text-xs text-[#6B4E37]">Currency</span>
      <div className="flex bg-white border border-[#E8E4DC] rounded-full p-0.5">
        <button
          onClick={() => setCurrency('EUR')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            currency === 'EUR' ? 'bg-[#1A2F4A] text-white' : 'text-[#6B4E37] hover:text-[#1A2F4A]'
          }`}
          data-testid="club-currency-eur">
          EUR (€)
        </button>
        <button
          onClick={() => setCurrency('INR')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            currency === 'INR' ? 'bg-[#1A2F4A] text-white' : 'text-[#6B4E37] hover:text-[#1A2F4A]'
          }`}
          data-testid="club-currency-inr">
          INR (₹)
        </button>
      </div>
    </div>
  );
}

function CodeOfConductModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-8" data-testid="conduct-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#999] hover:text-[#1A2F4A] transition-colors" data-testid="conduct-modal-close">
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-bold text-[#1A2F4A] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
          Code de conduite
        </h3>

        <div className="space-y-3 mb-8">
          {[
            'Respect all participants at all times',
            'No foul or inappropriate language',
            'No discrimination (gender, race, nationality, etc.)',
            'No body shaming or offensive remarks',
            'Do not share personal contact details without consent',
            'Maintain a respectful and safe environment',
            'Follow host instructions during sessions',
          ].map((rule, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#1A2F4A]">{rule}</p>
            </div>
          ))}
        </div>

        <div className="border-t border-[#E8E4DC] pt-6">
          <h4 className="text-sm font-bold text-[#1A2F4A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Moderation Policy
          </h4>
          <ul className="space-y-2">
            {[
              'The host may mute or remove participants if needed',
              '3 warnings rule applies',
              'Repeated violations may result in removal from the club',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#6B4E37]">
                <Shield className="w-4 h-4 text-[#1A2F4A] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function PreferenceForm() {
  const [form, setForm] = useState({ days: 'Both', time: 'Evening', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/api/club/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch {}
    setSubmitted(true);
    setLoading(false);
  };

  if (submitted) {
    return (
      <section className="py-14" data-testid="preference-success">
        <div className="max-w-[500px] mx-auto px-8 text-center">
          <div className="bg-white border border-[#E8E4DC] rounded-xl p-8">
            <CheckCircle className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <p className="text-[#1A2F4A] font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Merci
            </p>
            <p className="text-sm text-[#6B4E37] mb-6">
              Your preferences have been recorded. The final schedule will be shared soon.
            </p>
            <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5 text-left text-sm text-[#6B4E37] leading-relaxed">
              <p className="font-semibold text-[#1A2F4A] mb-2">Bonjour</p>
              <p className="mb-2">As we prepare to launch the Francophone Club, we'd love your input.</p>
              <p className="mb-1">Please share your preferred:</p>
              <ul className="list-disc list-inside mb-2 space-y-0.5">
                <li>Days (weekdays / weekends)</li>
                <li>Time (morning / afternoon / evening)</li>
              </ul>
              <p>Based on responses, we will finalize a fixed weekly schedule and inform you in advance.</p>
              <p className="mt-2 font-medium text-[#1A2F4A]">Merci</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14" data-testid="preference-form-section">
      <div className="max-w-[500px] mx-auto px-8">
        <h2
          className="text-xl font-bold text-[#1A2F4A] text-center mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}>
          Choisissez vos disponibilités
        </h2>
        <p className="text-sm text-[#6B4E37] text-center mb-2">
          Before finalizing session timings, we invite you to share your preferred days and time.
        </p>
        <p className="text-xs text-[#999] text-center mb-8 italic">
          Based on responses, a fixed weekly schedule will be created and shared in advance.
        </p>

        <form onSubmit={handleSubmit} className="bg-white border border-[#E8E4DC] rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#1A2F4A] mb-2">Preferred Days</label>
            <div className="flex gap-2">
              {['Weekdays', 'Weekends', 'Both'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, days: opt }))}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${
                    form.days === opt
                      ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                      : 'bg-white text-[#6B4E37] border-[#E8E4DC] hover:border-[#D4AF37]'
                  }`}
                  data-testid={`pref-days-${opt.toLowerCase()}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A2F4A] mb-2">Preferred Time</label>
            <div className="flex gap-2">
              {['Morning', 'Afternoon', 'Evening'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, time: opt }))}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${
                    form.time === opt
                      ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                      : 'bg-white text-[#6B4E37] border-[#E8E4DC] hover:border-[#D4AF37]'
                  }`}
                  data-testid={`pref-time-${opt.toLowerCase()}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">Email (optional)</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
              data-testid="pref-email"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="pref-submit">
            {loading ? 'Envoi...' : 'Envoyer mes préférences'}
            {!loading && <Send className="w-3.5 h-3.5" />}
          </button>
        </form>
      </div>
    </section>
  );
}

export default function ClubFrancophone() {
  const { currency, setCurrency } = useCurrency();
  const [showRules, setShowRules] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const price = currency === 'INR' ? '₹900' : '€15';

  return (
    <div className="min-h-screen bg-[#FAF7F0]">

      {/* Header */}
      <section className="pt-20 pb-10" data-testid="club-header">
        <div className="max-w-[700px] mx-auto px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl font-bold text-[#1A2F4A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="club-title">
            Le Club Francophone
          </h1>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-14 bg-[#D4AF37]/60"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/60"></div>
            <div className="h-px w-14 bg-[#D4AF37]/60"></div>
          </div>
          <p className="text-base text-[#6B4E37] leading-relaxed mb-3">
            Rejoignez des conversations réelles avec des enseignants et apprenants.
          </p>
          <p className="text-sm text-[#999] italic" data-testid="club-guidance">
            Practice speaking regularly with real people in a relaxed environment.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-14" data-testid="club-intro">
        <div className="max-w-[600px] mx-auto px-8 text-center">
          <p className="text-sm text-[#6B4E37] leading-relaxed mb-3">
            Most learners study French but rarely get the opportunity to speak.
          </p>
          <p className="text-sm text-[#6B4E37] leading-relaxed mb-3">
            Le Club Francophone provides a structured environment where learners can practice speaking regularly through live group conversations.
          </p>
          <p className="text-sm text-[#1A2F4A] font-medium italic">
            This is where French becomes natural — through real interaction.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* Comment ça fonctionne */}
      <section className="py-14" data-testid="club-how-it-works">
        <div className="max-w-[600px] mx-auto px-8">
          <h2
            className="text-xl font-bold text-[#1A2F4A] text-center mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Comment ça fonctionne
          </h2>
          <div className="space-y-3">
            {[
              { icon: Clock, text: '3 live sessions per week' },
              { icon: Clock, text: 'Each session lasts 60 minutes' },
              { icon: Users, text: 'Small groups (6–10 learners maximum)' },
              { icon: MessageCircle, text: 'One theme per session' },
              { icon: Sparkles, text: 'Guided but relaxed conversation' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-[#E8E4DC] rounded-xl px-5 py-3.5">
                <item.icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <p className="text-sm text-[#1A2F4A]">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#999] text-center mt-4 italic">
            Limited seats per group to ensure quality speaking time for each participant.
          </p>
        </div>
      </section>

      {/* Pourquoi rejoindre le club */}
      <section className="pb-14" data-testid="club-why-join">
        <div className="max-w-[550px] mx-auto px-8 text-center">
          <h2
            className="text-xl font-bold text-[#1A2F4A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Pourquoi rejoindre le club
          </h2>
          <div className="space-y-3">
            {[
              'Build fluency through regular speaking',
              'Gain confidence in a supportive environment',
              'Move beyond passive learning',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-left bg-white border border-[#E8E4DC] rounded-xl px-5 py-3.5">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <p className="text-sm text-[#1A2F4A]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages membres */}
      <section className="pb-14" data-testid="club-member-benefits">
        <div className="max-w-[600px] mx-auto px-8">
          <h2
            className="text-xl font-bold text-[#1A2F4A] text-center mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Avantages membres
          </h2>
          <p className="text-sm text-[#6B4E37] text-center mb-6 italic">
            Designed to help you speak consistently, improve faster, and stay engaged in your French learning journey.
          </p>
          <div className="space-y-2.5">
            {[
              'Access to 3 live group speaking sessions per week',
              'Small group interaction (6–10 learners maximum) ensuring active participation',
              'Priority access when booking one-to-one teacher sessions',
              'Discounted rates on private teacher sessions (members receive a reduced price)',
              'One exclusive group masterclass per month (members-only session with focused theme)',
              'One optional correction per month (short audio or text feedback)',
              'Access to curated discussion topics and guided conversation themes',
              'Literature-based discussions to explore language and culture',
              'Expression through translation exercises to refine clarity and nuance',
            ].map((benefit, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-[#E8E4DC] rounded-xl px-5 py-3">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#1A2F4A]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* Abonnement */}
      <section className="py-14" data-testid="club-pricing">
        <div className="max-w-[500px] mx-auto px-8 text-center">
          <h2
            className="text-xl font-bold text-[#1A2F4A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Abonnement
          </h2>
          <div className="mb-4">
            <CurrencyToggle currency={currency} setCurrency={setCurrency} />
          </div>
          <div className="bg-white border border-[#E8E4DC] rounded-xl p-8 mb-3">
            <p className="text-4xl font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="club-price">
              {price}
            </p>
            <p className="text-sm text-[#6B4E37]">/ month</p>
          </div>
          <p className="text-xs text-[#999] italic">
            Includes access to all weekly sessions.
          </p>
        </div>
      </section>

      {/* Free Trial */}
      <section className="pb-14" data-testid="club-free-trial">
        <div className="max-w-[500px] mx-auto px-8 text-center">
          <div className="bg-[#1A2F4A] rounded-xl px-6 py-5">
            <p className="text-white text-sm font-semibold">
              Try your first session for free.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* Preference Collection */}
      <PreferenceForm />

      {/* Divider */}
      <div className="max-w-[400px] mx-auto"><div className="h-px bg-[#E8E4DC]"></div></div>

      {/* CTA + Code of Conduct */}
      <section className="py-14" data-testid="club-cta">
        <div className="max-w-[500px] mx-auto px-8 text-center">
          <label className="flex items-start gap-3 justify-center mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 rounded border-[#E8E4DC] text-[#D4AF37] focus:ring-[#D4AF37]"
              data-testid="conduct-checkbox"
            />
            <span className="text-sm text-[#6B4E37] text-left">
              I agree to the <button onClick={() => setShowRules(true)} className="text-[#1A2F4A] font-semibold underline underline-offset-2 hover:text-[#D4AF37] transition-colors" data-testid="conduct-link">Code of Conduct</button>
            </span>
          </label>
          <Link
            to="/pricing"
            aria-disabled={!accepted}
            onClick={(e) => { if (!accepted) e.preventDefault(); }}
            className={`bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] px-8 py-3 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-2 ${!accepted ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
            data-testid="cta-rejoindre">
            Rejoindre le Club — €5/mois
            <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-4">
            <p className="text-xs text-[#999] mb-2">Before joining, please read and accept our Code of Conduct.</p>
            <button
              onClick={() => setShowRules(true)}
              className="text-xs font-medium text-[#1A2F4A] border border-[#E8E4DC] px-4 py-1.5 rounded-full hover:border-[#D4AF37] transition-colors"
              data-testid="btn-lire-regles">
              Lire les règles
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E8E4DC] py-10">
        <div className="max-w-[700px] mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>SELORA</span>
            <span className="text-xs text-[#6B4E37]">French Academy</span>
          </Link>
          <p className="text-xs text-[#999]">© 2026 Selora French Academy</p>
        </div>
      </footer>

      {/* Code of Conduct Modal */}
      <CodeOfConductModal open={showRules} onClose={() => setShowRules(false)} />
    </div>
  );
}
